import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { initiativesAPI } from '../lib/api';
import { convertGoogleDriveUrl } from '../lib/imageUtils';

const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1597132441547-a4fcd316da61?crop=entropy&cs=srgb&fm=jpg&q=85";

export default function Initiatives() {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInitiatives = async () => {
      try {
        const response = await initiativesAPI.getAll();
        setInitiatives(response.data);
      } catch (error) {
        console.error('Failed to fetch initiatives:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInitiatives();
  }, []);

  const statusColors = {
    ongoing: 'bg-green-100 text-green-800 border-green-200',
    completed: 'bg-blue-100 text-blue-800 border-blue-200',
    upcoming: 'bg-amber-100 text-amber-800 border-amber-200'
  };

  return (
    <div className="paper-texture min-h-screen" data-testid="initiatives-page">
      {/* Header */}
      <section className="py-16 md:py-24 bg-primary">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-accent font-medium text-sm uppercase tracking-widest">Our Work</span>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary-foreground mt-4 mb-6">
              Initiatives & Projects
            </h1>
            <p className="text-lg text-primary-foreground/80">
              Discover the ongoing and completed heritage projects that document and preserve 
              Jabalpur's rich cultural legacy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Initiatives List */}
      <section className="py-12 md:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          {loading ? (
            <div className="space-y-8">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse flex gap-8">
                  <div className="bg-muted h-48 w-1/3 flex-shrink-0"></div>
                  <div className="flex-1">
                    <div className="bg-muted h-6 w-1/4 mb-4"></div>
                    <div className="bg-muted h-8 w-3/4 mb-4"></div>
                    <div className="bg-muted h-4 w-full mb-2"></div>
                    <div className="bg-muted h-4 w-2/3"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : initiatives.length > 0 ? (
            <div className="space-y-12">
              {initiatives.map((initiative, idx) => (
                <motion.article
                  key={initiative.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className="group"
                >
                  <Link 
                    to={`/initiatives/${initiative.id}`}
                    className="grid md:grid-cols-3 gap-8 items-start"
                    data-testid={`initiative-${initiative.id}`}
                  >
                    <div className="img-zoom border border-border">
                      <img 
                        src={convertGoogleDriveUrl(initiative.featured_image) || PLACEHOLDER_IMAGE}
                        alt={initiative.title}
                        className="w-full h-48 md:h-56 object-cover"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <span className={`inline-block px-3 py-1 text-xs font-medium uppercase tracking-wider border ${statusColors[initiative.status] || statusColors.ongoing} mb-4`}>
                        {initiative.status}
                      </span>
                      <h2 className="font-heading text-2xl md:text-3xl font-semibold text-foreground mb-4 group-hover:text-accent transition-colors duration-300">
                        {initiative.title}
                      </h2>
                      <p className="text-muted-foreground leading-relaxed mb-4">
                        {initiative.description}
                      </p>
                      <span className="inline-flex items-center text-accent font-medium">
                        Learn More →
                      </span>
                    </div>
                  </Link>
                  {idx < initiatives.length - 1 && (
                    <div className="gold-line mt-12"></div>
                  )}
                </motion.article>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <h3 className="font-heading text-2xl text-foreground mb-4">No Initiatives Yet</h3>
              <p className="text-muted-foreground">
                Check back soon for updates on our heritage projects.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Coming Soon */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <p className="text-sm text-muted-foreground italic">
            More initiatives coming soon...
          </p>
        </div>
      </section>
    </div>
  );
}
